import type {
  SafeError as SharedSafeError,
} from "../../supabase/functions/_shared/safe-error-contract";
import {
  SAFE_ERROR_CODES,
  toClientError as mapToSharedClientError,
} from "../../supabase/functions/_shared/safe-error-contract";

const SANITIZED_TOKEN_REPLACEMENT = "[redacted]";

const SENSITIVE_PATTERNS: Array<{ test: RegExp }> = [
  { test: /(?:bearer|token|apikey|api_key|authorization|cookie|session_id|request_id|trace_id|idempotency_key)\s*[:=]\s*[^\s;"]+/gi },
  { test: /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g },
  { test: /\b[a-f0-9]{24,}\b/gi },
  { test: /\b[0-9a-z]{16,}[-][0-9a-z]{16,}\b/gi },
  { test: /\beyJ[a-zA-Z0-9-_]+\.[a-zA-Z0-9-_]+\.[a-zA-Z0-9-_]+\b/g },
  { test: /\s+at\s+[^\n]+\([^)]+\)/gi },
];

type SafeErrorCode = (typeof SAFE_ERROR_CODES)[keyof typeof SAFE_ERROR_CODES];

export type SafeError = SharedSafeError;

type LoggerPayload = Record<string, unknown> | string | number | null | undefined;

const DEFAULT_REQUEST_ID = "unknown";

const toRequestId = (requestHeaders?: Headers): string => {
  if (!requestHeaders) return DEFAULT_REQUEST_ID;
  return (
    requestHeaders.get("x-request-id") ||
    requestHeaders.get("x-correlation-id") ||
    requestHeaders.get("x-trace-id") ||
    DEFAULT_REQUEST_ID
  );
};

const sanitizeValue = (value: unknown): unknown => {
  if (value == null) return value;
  if (Array.isArray(value)) return value.map(sanitizeValue);

  if (typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, nested]) => {
        if (
          /(token|authorization|cookie|session|email|request_id|trace_id|correlation|idempotency|refresh|supabase|apikey|api_key)/i.test(
            key
          )
        ) {
          return [key, SANITIZED_TOKEN_REPLACEMENT];
        }
        return [key, sanitizeValue(nested)];
      }),
    );
  }

  if (typeof value === "string") {
    return sanitizeForUser(value);
  }

  return value;
};

export const sanitizeForUser = (value: unknown): string => {
  const base = typeof value === "string" ? value : String(value);
  return SENSITIVE_PATTERNS.reduce(
    (next, pattern) => next.replace(pattern, SANITIZED_TOKEN_REPLACEMENT),
    base,
  )
    .replace(/\n/g, " ")
    .replace(/\r/g, " ")
    .replace(/\s+at\s+[^\n]+\([^)]+\)/gi, " [redacted stack] ")
    .replace(/\/src\/[^\\s"]+/gi, "[redacted path]")
    .slice(0, 2000);
};

export const toClientError = (error: unknown, requestHeaders?: Headers, fallbackCode: SafeErrorCode = SAFE_ERROR_CODES.INTERNAL_ERROR): SafeError => {
  return mapToSharedClientError(error, requestHeaders, fallbackCode);
};

type LogMethod = "debug" | "info" | "warn" | "error";

const writeLog = (level: LogMethod, message: string, data?: LoggerPayload) => {
  const safeMessage = sanitizeForUser(message);
  const safeData = sanitizeValue(data ?? {});
  const payload = JSON.stringify({
    level,
    ts: new Date().toISOString(),
    message: safeMessage,
    data: safeData,
  });

  // Only log to console in development mode
  // In production, logs should be sent to external service (e.g., Sentry, LogRocket)
  if (!import.meta.env.PROD) {
    if (level === "error") {
      console.error(payload);
      return;
    }
    if (level === "warn") {
      console.warn(payload);
      return;
    }
    if (level === "info") {
      console.info(payload);
      return;
    }
    console.debug(payload);
  }
  
  // TODO: Send to external logging/monitoring service in production
  // Example: Sentry.captureMessage(safeMessage, { level, extra: safeData });
};

export const logger = {
  debug: (message: string, data?: LoggerPayload) => writeLog("debug", message, data),
  info: (message: string, data?: LoggerPayload) => writeLog("info", message, data),
  warn: (message: string, data?: LoggerPayload) => writeLog("warn", message, data),
  error: (message: string, data?: LoggerPayload) => writeLog("error", message, data),
  sanitizeForUser,
  toClientError,
  requestIdFromHeaders: toRequestId,
};
